/**
 * EmployeeFormModal — create or edit an employee (Admin).
 *
 * Creating an employee also creates their login account (role + initial password).
 * Supports multiple emails and phone numbers; primary email is the login email.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../shell/Modal.js';
import { Button } from '../../../shell/Button.js';
import { Input, Select } from '../../../shell/Field.js';
import { useCreateEmployee, useUpdateEmployee } from '../api.js';
import { EmployeeSelect } from './EmployeeSelect.js';
import {
  ContactListEditor,
  newContactDraft,
  type ContactDraft,
} from '../../contacts/ContactListEditor.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { ROLE, type Role } from '@shared/enums/index.js';
import type { Employee } from '@shared/types';

type EmployeeFormValues = {
  name: string;
  legalName: string;
  department: string;
  title: string;
  hireDate: string;
  managerId: string;
  employmentStatus: 'Active' | 'OnLeave' | 'Terminated';
  employeeNumber: string;
  workLocation: 'OnSite' | 'Remote';
  keyFobSerial: string;
  role: Role;
  password: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided, modal is in edit mode. */
  employee?: Employee | null;
}

function toDateIso(day: string): string {
  if (!day) return new Date().toISOString();
  return new Date(`${day}T00:00:00.000Z`).toISOString();
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function draftsFromEmployee(employee: Employee | null | undefined): {
  emails: ContactDraft[];
  phones: ContactDraft[];
} {
  if (!employee) {
    return {
      emails: [newContactDraft('email', { isPrimary: true })],
      phones: [],
    };
  }
  const contacts = employee.contacts?.length
    ? employee.contacts
    : [
        {
          id: 'legacy-email',
          kind: 'email' as const,
          label: 'Work',
          value: employee.email,
          isPrimary: true,
          sortOrder: 0,
        },
        ...(employee.phone
          ? [{
              id: 'legacy-phone',
              kind: 'phone' as const,
              label: 'Work',
              value: employee.phone,
              isPrimary: true,
              sortOrder: 0,
            }]
          : []),
      ];
  return {
    emails: contacts
      .filter((c) => c.kind === 'email')
      .map((c) =>
        newContactDraft('email', {
          label: c.label,
          value: c.value,
          isPrimary: c.isPrimary,
        }),
      ),
    phones: contacts
      .filter((c) => c.kind === 'phone')
      .map((c) =>
        newContactDraft('phone', {
          label: c.label,
          value: c.value,
          isPrimary: c.isPrimary,
        }),
      ),
  };
}

export function EmployeeFormModal({ open, onClose, employee }: Props) {
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const isEdit = Boolean(employee);
  const [emails, setEmails] = useState<ContactDraft[]>(() => draftsFromEmployee(null).emails);
  const [phones, setPhones] = useState<ContactDraft[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EmployeeFormValues>({
    defaultValues: {
      name: '',
      legalName: '',
      department: '',
      title: '',
      hireDate: '',
      managerId: '',
      employmentStatus: 'Active',
      employeeNumber: '',
      workLocation: 'OnSite',
      keyFobSerial: '',
      role: 'Employee',
      password: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    const drafts = draftsFromEmployee(employee);
    setEmails(drafts.emails);
    setPhones(drafts.phones);
    if (employee) {
      reset({
        name: employee.name,
        legalName: employee.legalName ?? '',
        department: employee.department,
        title: employee.title,
        hireDate: toDateInput(employee.hireDate),
        managerId: employee.managerId ?? '',
        employmentStatus: employee.employmentStatus,
        employeeNumber: employee.employeeNumber ?? '',
        workLocation: employee.workLocation ?? 'OnSite',
        keyFobSerial: '',
        role: 'Employee',
        password: '',
      });
    } else {
      reset({
        name: '',
        legalName: '',
        department: '',
        title: '',
        hireDate: '',
        managerId: '',
        employmentStatus: 'Active',
        employeeNumber: '',
        workLocation: 'OnSite',
        keyFobSerial: '',
        role: 'Employee',
        password: '',
      });
    }
  }, [open, employee, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: EmployeeFormValues) => {
    const filledEmails = emails.filter((e) => e.value.trim());
    if (filledEmails.length === 0) {
      toast('error', 'At least one email is required.');
      return;
    }
    if (!filledEmails.some((e) => e.isPrimary)) {
      filledEmails[0]!.isPrimary = true;
    }
    const filledPhones = phones.filter((p) => p.value.trim());
    if (filledPhones.length > 0 && !filledPhones.some((p) => p.isPrimary)) {
      filledPhones[0]!.isPrimary = true;
    }

    const contacts = [
      ...filledEmails.map((e) => ({
        kind: 'email' as const,
        label: e.label,
        value: e.value.trim(),
        isPrimary: e.isPrimary,
      })),
      ...filledPhones.map((p) => ({
        kind: 'phone' as const,
        label: p.label,
        value: p.value.trim(),
        isPrimary: p.isPrimary,
      })),
    ];

    const profile = {
      name: values.name.trim(),
      legalName: values.legalName.trim() || null,
      department: values.department.trim(),
      title: values.title.trim(),
      hireDate: toDateIso(values.hireDate),
      managerId: values.managerId.trim() ? values.managerId.trim() : null,
      employmentStatus: values.employmentStatus,
      employeeNumber: values.employeeNumber.trim() || null,
      workLocation: values.workLocation,
      contacts,
    };

    try {
      if (isEdit && employee) {
        await update.mutateAsync({ id: employee.id, body: profile });
        toast('success', 'Employee updated.');
      } else {
        await create.mutateAsync({
          ...profile,
          keyFobSerial: values.keyFobSerial.trim() || undefined,
          role: values.role,
          password: values.password,
        });
        toast('success', 'Employee and login account created.');
      }
      handleClose();
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.body.fields
            ? Object.values(err.body.fields).join(' ')
            : err.body.message
          : isEdit
            ? 'Failed to update employee.'
            : 'Failed to create employee.';
      toast('error', message);
    }
  };

  const pending = create.isPending || update.isPending;
  const managerId = watch('managerId');
  const workLocation = watch('workLocation');

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? 'Edit employee' : 'Add employee'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Preferred / known-as name"
          placeholder="Yany Sales"
          error={errors.name?.message}
          {...register('name', { required: 'Name is required.' })}
        />
        <Input
          label="Legal name (if different)"
          placeholder="Yanelys Sales"
          {...register('legalName')}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Employee #"
            placeholder="EMP-0473"
            {...register('employeeNumber')}
          />
          <Select label="Work location" {...register('workLocation')}>
            <option value="OnSite">On-site</option>
            <option value="Remote">Remote</option>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Department"
            placeholder="Retail"
            error={errors.department?.message}
            {...register('department', { required: 'Department is required.' })}
          />
          <Input
            label="Title"
            placeholder="Sales Associate"
            error={errors.title?.message}
            {...register('title', { required: 'Title is required.' })}
          />
        </div>
        <Input
          label="Hire date"
          type="date"
          error={errors.hireDate?.message}
          {...register('hireDate', { required: 'Hire date is required.' })}
        />
        {!isEdit && workLocation === 'OnSite' && (
          <Input
            label="Key Fob serial (optional)"
            placeholder="8616306"
            {...register('keyFobSerial')}
          />
        )}
        <EmployeeSelect
          label="Manager (optional)"
          value={managerId}
          onChange={(id) => setValue('managerId', id)}
          excludeIds={employee ? [employee.id] : []}
          directory
          placeholder="No manager"
          activeOnly
        />
        <Select label="Status" {...register('employmentStatus')}>
          <option value="Active">Active</option>
          <option value="OnLeave">On Leave</option>
          <option value="Terminated">Terminated</option>
        </Select>

        <div className="space-y-4 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Contact info
          </p>
          <ContactListEditor kind="email" items={emails} onChange={setEmails} />
          <ContactListEditor kind="phone" items={phones} onChange={setPhones} />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            The primary email is used for the login account.
          </p>
        </div>

        {!isEdit && (
          <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Login account</p>
            <Select label="Role" {...register('role', { required: 'Role is required.' })}>
              {ROLE.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Select>
            <Input
              label="Initial password"
              type="password"
              placeholder="Min 8 characters"
              error={errors.password?.message}
              {...register('password', {
                required: 'Initial password is required.',
                minLength: { value: 8, message: 'Password must be at least 8 characters.' },
              })}
            />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button type="submit" loading={pending}>{isEdit ? 'Save Changes' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  );
}
